import { IRedemption } from '@interfaces/redemption';
import { RECENT_TXN_LIMIT_MIN, REDEMPTION_TYPE } from '@constants/transaction.constants';
import { DealTag, PrivilegeTag } from '../Tag';
import { getDateTime12H, isWithinLastMinutes } from '@helpers/date-time.helper';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@constants/route.constant';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/state';
import StatusDot from '@assets/images/status-dot.svg';
import { DateTime } from 'luxon';
import { markRedemptionAsOld } from '@/state/slice/redemptionsSlice';
import { TIME_INTERVAL } from '@constants/date-time.constants';

type RedemptionTemplateProps = {
	redemption: IRedemption;
};

export function RedemptionTemplate({ redemption }: RedemptionTemplateProps) {
	const [showHighlight, setShowHighlight] = useState(false);
	const [showStatusDot, setShowStatusDot] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();

	const getTag = (redemption: IRedemption) => {
		switch (redemption.type) {
			case REDEMPTION_TYPE.DEAL:
				return <DealTag />;
			case REDEMPTION_TYPE.PRIVILEGE:
				return <PrivilegeTag />;
			default:
				return <></>;
		}
	};

	const navigateTo = ({ id, type }: { id: string; type: string }) => {
		navigate(`/${ROUTES.REDEMPTIONS}/${id}?type=${type}`, { state: { previousRoute: location.pathname + location.search } });
	};

	const isNewWithinTwoMinutes = (): boolean => {
		if (redemption.isNew) return isWithinLastMinutes(DateTime.fromISO(redemption.redeemDate), RECENT_TXN_LIMIT_MIN);
		return false;
	};

	useEffect(() => {
		let interval: NodeJS.Timeout;
		if (redemption?.isNew) {
			setShowHighlight(isNewWithinTwoMinutes());
			setShowStatusDot(isNewWithinTwoMinutes());
			interval = setInterval(() => {
				setShowStatusDot(isNewWithinTwoMinutes());
			}, TIME_INTERVAL.ONE_SECOND);
		}
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		let timeout: NodeJS.Timeout;
		if (showHighlight) {
			timeout = setTimeout(() => {
				setShowHighlight(false);
			}, TIME_INTERVAL.THREE_SECONDS);
		}
		return () => clearTimeout(timeout);
	}, [showHighlight]);

	return (
		<div className='relative'>
			<div
				onClick={() => {
					dispatch(markRedemptionAsOld(redemption.redemptionId));
					navigateTo({ id: redemption.redemptionId, type: redemption.type });
				}}
				className='cursor-pointer'>
				{redemption.isNew && showHighlight && (
					<div data-testid='highlight' className='absolute top-0 -left-6  w-[calc(100%+48px)] h-full bg-[#7AFA5699] animate-fadeInOut'></div>
				)}
				<div className='py-4'>
					<div className='text-blue-grey-dark text-xl font-semibold truncate items-center gap-1'>
						{getTag(redemption)}
						<div className='flex items-center gap-1 truncate'>
							{redemption.isNew && showStatusDot && (
								<div data-testid='status-dot'>
									<StatusDot />
								</div>
							)}
							{redemption.title}
						</div>
					</div>
					<div className='w-100 text-grey-dark'>
						{getDateTime12H(redemption.redeemDate)}
						<span className='px-1'>|</span>
						{redemption.pos}
					</div>
				</div>
				<hr className='border-blue-grey-lighter' />
			</div>
		</div>
	);
}
